'use client';

import { usePathname } from 'next/navigation';
import Link from 'next/link';
import clsx from 'clsx';
import { Popover } from 'antd';
import {
  MailOutlined,
  PhoneOutlined,
  IdcardOutlined,
  ApartmentOutlined,
  SolutionOutlined,
  UserOutlined,
  LogoutOutlined,
} from '@ant-design/icons';
import { useCurrentUser } from '@/contexts';
import { UserAvatar } from '@/components/user/UserAvatar';

const tabs = [
  { label: 'Danh bạ', href: '/danh-ba' },
  { label: 'Cơ cấu tổ chức', href: '/co-cau-to-chuc' },
  { label: 'Hồ sơ cá nhân', href: '/ho-so-ca-nhan' },
  { label: 'Thường xuyên liên hệ', href: '/thuong-xuyen-lien-he' },
];

export function Header() {
  const pathname = usePathname();
  const currentUser = useCurrentUser();

  const userInfoContent = currentUser && (
    <div className="w-80 p-2">
      {/* Header with Avatar and Name */}
      <div className="mb-4 flex items-center gap-3 border-b border-slate-200 pb-4">
        <UserAvatar name={currentUser.fullName} avatar={currentUser.avatar} size="lg" />
        <div>
          <div className="text-base font-semibold text-slate-900">{currentUser.fullName}</div>
          {currentUser.code && (
            <div className="text-xs text-text-secondary">Mã NV: {currentUser.code}</div>
          )}
        </div>
      </div>

      {/* User Details */}
      <div className="space-y-3">
        {currentUser.jobTitle && (
          <div className="flex items-start gap-2">
            <SolutionOutlined className="mt-0.5 text-primary-light" />
            <div className="flex-1">
              <div className="text-xs text-text-secondary">Chức vụ</div>
              <div className="text-sm text-slate-700">{currentUser.jobTitle}</div>
            </div>
          </div>
        )}

        {currentUser.department?.name && (
          <div className="flex items-start gap-2">
            <ApartmentOutlined className="mt-0.5 text-primary-light" />
            <div className="flex-1">
              <div className="text-xs text-text-secondary">Phòng ban</div>
              <div className="text-sm text-slate-700">{currentUser.department.name}</div>
            </div>
          </div>
        )}

        {currentUser.workEmail && (
          <div className="flex items-start gap-2">
            <MailOutlined className="mt-0.5 text-primary-light" />
            <div className="flex-1">
              <div className="text-xs text-text-secondary">Email</div>
              <a
                href={`mailto:${currentUser.workEmail}`}
                className="text-sm text-text-link hover:underline"
              >
                {currentUser.workEmail}
              </a>
            </div>
          </div>
        )}

        {currentUser.workPhone && (
          <div className="flex items-start gap-2">
            <PhoneOutlined className="mt-0.5 text-primary-light" />
            <div className="flex-1">
              <div className="text-xs text-text-secondary">Điện thoại</div>
              <a
                href={`tel:${currentUser.workPhone}`}
                className="text-sm text-text-link hover:underline"
              >
                {currentUser.workPhone}
              </a>
            </div>
          </div>
        )}
      </div>

      {/* View Profile Link */}
      <div className="mt-4 space-y-2 border-t border-slate-200 pt-4">
        <Link
          href="/ho-so-ca-nhan"
          className="flex items-center justify-center gap-2 rounded-md bg-primary-light px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary"
        >
          <UserOutlined />
          Xem hồ sơ cá nhân
        </Link>
        <button
          type="button"
          onClick={() => {
            void currentUser.logout();
          }}
          className="flex w-full items-center justify-center gap-2 rounded-md border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-600 transition-colors hover:border-red-300 hover:bg-red-50"
        >
          <LogoutOutlined />
          Đăng xuất
        </button>
      </div>
    </div>
  );

  return (
    <header className="border-b border-slate-200 bg-white">
      <div className="mx-auto flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3">
          <>
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="160 149 211 211"><g><polygon fill="#010101" points="269.72 207.23 281.06 207.28 269.29 207.28 269.33 207.23 269.72 207.23"></polygon><polygon fill="#010101" points="331.04 187.94 311.73 207.35 302.34 207.37 281.06 207.28 311.48 207.28 330.98 187.93 331.04 187.94"></polygon><path fill="#3a62ae" d="m360.01,266.31c-.02.48-.07,1.03-.13,1.64-.08.76-.19,1.64-.35,2.59-.34,2.12-.88,4.69-1.68,7.59-.07.29-.14.59-.23.87-10.83,40.55-47.82,70.41-91.8,70.41-52.48,0-95.03-42.55-95.03-95.03s42.54-95.03,95.03-95.03h93.98l-28.82,28.59-19.5,19.35h-30.42l-11.35-.05h-1.39c.13.02.25.03.38.05h-3.37s-41.03-.29-45.61,45.91c0,0-2.44,35.36,36.29,46.87,0,0,8.82,2.41,19.92.34.55-.08,1.12-.21,1.68-.33,4.27-1.09,8.31-2.78,12.02-4.95.02-.01.05-.03.08-.04,3.48-2.04,6.67-4.5,9.51-7.32,2.35-2.35,4.46-4.95,6.29-7.76h-49.24l39.74-22.94h53.99c5.24.09,10.21,2.8,10.02,9.25Z"></path><path fill="#3a62ae" d="m281.06,207.28h-12.36c-.13-.02-.25-.03-.38-.05h1.39l11.35.05Z"></path><path fill="#010101" d="m353.81,228.06s-17.29,14.31-45.7,1.4c0,0-6.37-2.9-11.41-8.2-1.14-1.2-2.29-2.38-3.57-3.42-3.94-3.23-12.34-9.19-22.82-10.39-.36-.04-.72-.08-1.08-.11l.07-.07h11.78l21.28.09,9.39-.02,19.31-19.41.36.45c6.11,6.9,18.69,23.09,22.41,39.67Z"></path><path fill="#010101" d="m281.06,207.28h-11.78l-.07.07c-.17-.02-.35-.05-.52-.07-.13-.02-.25-.03-.38-.05h1.39l11.35.05Z"></path><path fill="#010101" d="m249.42,232.51v41.52c0,3.19,3.45,5.18,6.21,3.59l35.95-20.76c2.76-1.59,2.76-5.58,0-7.17l-35.95-20.76c-2.76-1.59-6.21.4-6.21,3.59Z"></path></g></svg>
          </>
          <div className="text-3xl font-bold text-primary">GIKGOLD</div>
          <div className="h-6 w-px bg-slate-300" />
          <span className="text-base font-medium text-slate-700">Danh bạ doanh nghiệp</span>
        </div>

        {/* User Avatar with Popover */}
        {currentUser && (
          <Popover content={userInfoContent} trigger="click" placement="bottomRight">
            <div className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1 transition-colors hover:bg-slate-50">
              <span className="text-base font-medium text-slate-700">{currentUser.fullName}</span>
              <UserAvatar name={currentUser.fullName} avatar={currentUser.avatar} size="sm" />
            </div>
          </Popover>
        )}
      </div>
      <nav className="mx-auto px-4">
        <div className="flex gap-8">
          {tabs.map((tab) => {
            const isActive = pathname === tab.href || pathname?.startsWith(tab.href + '/');
            return (
              <Link
                key={tab.href}
                href={tab.href as any}
                className={clsx(
                  'border-b-2 pb-2 pt-1 text-base font-medium transition-colors',
                  isActive
                    ? 'border-primary-light text-primary-light'
                    : 'border-transparent text-slate-500 hover:text-slate-700',
                )}
              >
                {tab.label}
              </Link>
            );
          })}
        </div>
      </nav>
    </header>
  );
}
